'use client';

// Ctrl+K / Cmd+K global search — command palette over the caller's own
// sessions, visualizations, documents, agents, and projects (GET
// /api/search?q=). New surface (no apps/web original): built on the existing
// shadcn/cmdk primitives in components/ui/command.tsx (already wired to
// `cmdk` — see CommandDialog), so this is just the data + keybinding + result
// wiring, not a new palette implementation.
//
// Opens on the global Cmd/Ctrl+K keydown OR the sidebar's search button
// (dispatches the `galexia:open-search` window event — kept decoupled from
// Sidebar/AppLayout props so this component is a single self-contained
// mount in client-layout.tsx).
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ChatCircle,
  SquaresFour,
  FilePdf,
  Robot,
  Folders,
  ImageSquare,
  Plus,
} from '@phosphor-icons/react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

interface SearchHit {
  id: string;
  label: string;
  subtitle?: string;
}

interface SearchResults {
  sessions: SearchHit[];
  visualizations: SearchHit[];
  documents: SearchHit[];
  agents: SearchHit[];
  projects: SearchHit[];
}

const EMPTY_RESULTS: SearchResults = {
  sessions: [],
  visualizations: [],
  documents: [],
  agents: [],
  projects: [],
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

export const OPEN_SEARCH_EVENT = 'galexia:open-search';

interface GlobalSearchProps {
  /** Wires a project hit to the sidebar's project filter (client-layout.tsx
   *  owns `activeProjectId`) instead of a route change — same "select, don't
   *  navigate" behavior the sidebar's own project list already uses. */
  onSelectProject?: (projectId: string | null) => void;
}

export function GlobalSearch({ onSelectProject }: GlobalSearchProps) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  // Cmd/Ctrl+K toggles the palette from anywhere in the app.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // The sidebar's search affordance opens the same palette via this event.
  useEffect(() => {
    function onOpenRequest() {
      setOpen(true);
    }
    window.addEventListener(OPEN_SEARCH_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_SEARCH_EVENT, onOpenRequest);
  }, []);

  // Reset on close so re-opening never flashes stale results.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(EMPTY_RESULTS);
      setLoading(false);
    }
  }, [open]);

  // Debounced fetch — only fires once the query clears the min length.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}`);
        if (requestId !== requestIdRef.current) return; // superseded by a newer keystroke
        if (!res.ok) {
          setResults(EMPTY_RESULTS);
          return;
        }
        const data = await res.json();
        setResults({
          sessions: data.sessions ?? [],
          visualizations: data.visualizations ?? [],
          documents: data.documents ?? [],
          agents: data.agents ?? [],
          projects: data.projects ?? [],
        });
      } catch {
        if (requestId === requestIdRef.current) setResults(EMPTY_RESULTS);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  const go = (href: string) => {
    setOpen(false);
    router.push(href);
  };

  const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;
  const hasResults =
    results.sessions.length > 0 ||
    results.visualizations.length > 0 ||
    results.documents.length > 0 ||
    results.agents.length > 0 ||
    results.projects.length > 0;

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Search"
      description="Jump to a chat, visualization, document, agent, or project."
    >
      <CommandInput
        placeholder="Search chats, visualizations, documents, agents, projects..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {!hasQuery && (
          <CommandGroup heading="Quick actions">
            <CommandItem onSelect={() => go('/')}>
              <Plus weight="bold" />
              New chat
            </CommandItem>
            <CommandItem onSelect={() => go('/documents')}>
              <FilePdf weight="duotone" />
              Documents
            </CommandItem>
            <CommandItem onSelect={() => go('/media')}>
              <ImageSquare weight="duotone" />
              Media
            </CommandItem>
          </CommandGroup>
        )}

        {hasQuery && !loading && !hasResults && (
          <CommandEmpty>No results for &ldquo;{query.trim()}&rdquo;.</CommandEmpty>
        )}
        {hasQuery && loading && !hasResults && <CommandEmpty>Searching…</CommandEmpty>}

        {results.sessions.length > 0 && (
          <CommandGroup heading="Chats">
            {results.sessions.map((hit) => (
              <CommandItem key={hit.id} value={`session-${hit.id}-${hit.label}`} onSelect={() => go(`/chat/${hit.id}`)}>
                <ChatCircle weight="duotone" />
                <span className="flex-1 truncate">{hit.label}</span>
                {hit.subtitle && <span className="text-xs text-muted-foreground">{hit.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.visualizations.length > 0 && (
          <CommandGroup heading="Visualizations">
            {results.visualizations.map((hit) => (
              <CommandItem key={hit.id} value={`viz-${hit.id}-${hit.label}`} onSelect={() => go(`/v/${hit.id}`)}>
                <SquaresFour weight="duotone" />
                <span className="flex-1 truncate">{hit.label}</span>
                {hit.subtitle && <span className="text-xs text-muted-foreground">{hit.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.documents.length > 0 && (
          <CommandGroup heading="Documents">
            {results.documents.map((hit) => (
              <CommandItem key={hit.id} value={`doc-${hit.id}-${hit.label}`} onSelect={() => go('/documents')}>
                <FilePdf weight="duotone" />
                <span className="flex-1 truncate">{hit.label}</span>
                {hit.subtitle && <span className="text-xs text-muted-foreground">{hit.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.agents.length > 0 && (
          <CommandGroup heading="Agents">
            {results.agents.map((hit) => (
              <CommandItem key={hit.id} value={`agent-${hit.id}-${hit.label}`} onSelect={() => go('/agents')}>
                <Robot weight="duotone" />
                <span className="flex-1 truncate">{hit.label}</span>
                {hit.subtitle && <span className="text-xs text-muted-foreground">{hit.subtitle}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results.projects.length > 0 && (
          <CommandGroup heading="Projects">
            {results.projects.map((hit) => (
              <CommandItem
                key={hit.id}
                value={`project-${hit.id}-${hit.label}`}
                onSelect={() => {
                  setOpen(false);
                  if (onSelectProject) onSelectProject(hit.id);
                  else router.push('/');
                }}
              >
                <Folders weight="duotone" />
                <span className="flex-1 truncate">{hit.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
